import { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Account,
  JournalDraftInput,
  JournalEntry,
  PeriodClosure,
  TrialBalanceRow,
  TrialBalanceTotals,
  BalanceCheckResult,
  UserProfile,
  Customer,
  Product,
  Coupon
} from './types';
import { API_ROUTES } from './lib/api-routes';
import { branding } from '../config/branding';
import { ProfileSetupModal } from './components/ProfileSetupModal';
import { usePermissions } from './lib/PermissionsContext';
import { Sidebar } from './components/Sidebar';
import { Breadcrumb } from './components/Breadcrumb';

// Lazy load all view components

const JournalView = lazy(() => import('./components/JournalView').then(m => ({ default: m.JournalView })));
const TransactionsView = lazy(() => import('./components/TransactionsView').then(m => ({ default: m.TransactionsView })));
const AccountsView = lazy(() => import('./components/AccountsView').then(m => ({ default: m.AccountsView })));
const ReportsView = lazy(() => import('./components/ReportsView').then(m => ({ default: m.ReportsView })));
const ReconciliationView = lazy(() => import('./components/ReconciliationView').then(m => ({ default: m.ReconciliationView })));

const KanbanBoardView = lazy(() => import('./components/oms/KanbanBoardView').then(m => ({ default: m.KanbanBoardView })));
const OrdersView = lazy(() => import('./components/oms/OrdersView').then(m => ({ default: m.OrdersView })));
const CustomersView = lazy(() => import('./components/oms/CustomersView').then(m => ({ default: m.CustomersView })));
const ProductsView = lazy(() => import('./components/oms/ProductsView').then(m => ({ default: m.ProductsView })));
const CouponsView = lazy(() => import('./components/CouponsView').then(m => ({ default: m.CouponsView })));
const InventoryView = lazy(() => import('./components/oms/InventoryView').then(m => ({ default: m.InventoryView })));

const ImportView = lazy(() => import('./components/ImportView').then(m => ({ default: m.ImportView })));
const EtsyImportView = lazy(() => import('./components/EtsyImportView').then(m => ({ default: m.EtsyImportView })));
const AuditLogView = lazy(() => import('./components/AuditLogView').then(m => ({ default: m.AuditLogView })));
const UsersView = lazy(() => import('./components/UsersView').then(m => ({ default: m.UsersView })));
const RolePermissionsView = lazy(() => import('./components/RolePermissionsView').then(m => ({ default: m.RolePermissionsView })));
const AdminSettingsView = lazy(() => import('./components/AdminSettingsView').then(m => ({ default: m.AdminSettingsView })));
const EtsyIntegrationView = lazy(() => import('./components/EtsyIntegrationView').then(m => ({ default: m.EtsyIntegrationView })));

const PrintersView = lazy(() => import('./components/workshop/PrintersView').then(m => ({ default: m.PrintersView })));
const SpoolsView = lazy(() => import('./components/workshop/SpoolsView').then(m => ({ default: m.SpoolsView })));
const PrintQueueView = lazy(() => import('./components/workshop/PrintLogView').then(m => ({ default: m.PrintLogView })));
const MaintenanceLogView = lazy(() => import('./components/workshop/MaintenanceLogView').then(m => ({ default: m.MaintenanceLogView })));
const WorkshopInventoryView = lazy(() => import('./components/workshop/WorkshopInventoryView').then(m => ({ default: m.WorkshopInventoryView })));
const ProfileView = lazy(() => import('./components/ProfileView').then(m => ({ default: m.ProfileView })));
const HelpView = lazy(() => import('./components/HelpView').then(m => ({ default: m.HelpView })));
const TimeTrackingView = lazy(() => import('./components/TimeTrackingView').then(m => ({ default: m.TimeTrackingView })));
const MileageView = lazy(() => import('./components/MileageView').then(m => ({ default: m.MileageView })));
const DashboardView = lazy(() => import('./components/DashboardView').then(m => ({ default: m.DashboardView })));
const FinancialDashboardView = lazy(() => import('./components/FinancialDashboardView').then(m => ({ default: m.FinancialDashboardView })));

// Enhanced Finance lazy components
const EnhancedFinanceDashboard = lazy(() => import('./components/EnhancedFinance/EnhancedFinanceDashboard').then(m => ({ default: m.EnhancedFinanceDashboard })));
const PeriodHealthDashboard = lazy(() => import('./components/EnhancedFinance/PeriodHealthDashboard').then(m => ({ default: m.PeriodHealthDashboard })));
const JournalEntriesWithSearch = lazy(() => import('./components/EnhancedFinance/JournalEntriesWithSearch').then(m => ({ default: m.JournalEntriesWithSearch })));
const PostedEntriesWithSearch = lazy(() => import('./components/EnhancedFinance/PostedEntriesWithSearch').then(m => ({ default: m.PostedEntriesWithSearch })));
const EntryTemplatesView = lazy(() => import('./components/EnhancedFinance/EntryTemplatesView').then(m => ({ default: m.EntryTemplatesView })));
const AccountReconciliationHub = lazy(() => import('./components/EnhancedFinance/AccountReconciliationHub').then(m => ({ default: m.AccountReconciliationHub })));
const EntryPostingAuditTrail = lazy(() => import('./components/EnhancedFinance/EntryPostingAuditTrail').then(m => ({ default: m.EntryPostingAuditTrail })));
const AccountBalanceHistory = lazy(() => import('./components/EnhancedFinance/AccountBalanceHistory').then(m => ({ default: m.AccountBalanceHistory })));
const BatchOperationsView = lazy(() => import('./components/EnhancedFinance/BatchOperationsView').then(m => ({ default: m.BatchOperationsView })));

// Loading fallback component
const RouteLoadingFallback = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
  </div>
);

/**
 * AppContent - Inner component that handles routing and layout
 * Separated from App to use router hooks
 */
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: permissionsLoading, checkAccess, ...permissions } = usePermissions();
  
  // Sidebar state - persisted in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem('sidebar-collapsed');
    return stored === 'true';
  });

  // Profile setup modal state
  const [showSetupModal, setShowSetupModal] = useState(false);
  
  // Data state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [journalDrafts, setJournalDrafts] = useState<JournalEntry[]>([]);
  const [postedEntries, setPostedEntries] = useState<JournalEntry[]>([]);
  const [trialRows, setTrialRows] = useState<TrialBalanceRow[]>([]);
  const [trialTotals, setTrialTotals] = useState<TrialBalanceTotals>({ debit: 0, credit: 0 });
  const [periods, setPeriods] = useState<PeriodClosure[]>([]);
  const [monthsWithEntries, setMonthsWithEntries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [postedLoading, setPostedLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [balanceCheck, setBalanceCheck] = useState<BalanceCheckResult | null>(null);

  // Persist sidebar state to localStorage
  useEffect(() => {
    // eslint-disable-next-line no-undef
    localStorage.setItem('sidebar-collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Persist last visited page to localStorage
  useEffect(() => {
    if (location.pathname !== '/') {
      // eslint-disable-next-line no-undef
      localStorage.setItem('last-visited-page', location.pathname);
    }
  }, [location.pathname]);

  // Restore last visited page on mount
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const lastPage = localStorage.getItem('last-visited-page');
    if (lastPage && lastPage !== '/' && location.pathname === '/') {
      navigate(lastPage, { replace: true });
    }
  }, [navigate, location.pathname]);

  // Data fetching functions
  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch('/api/profile');
      if (res.ok) {
        const data = await res.json() as { profile: UserProfile };
        setUserProfile(data.profile);
        if (!data.profile.profileInitialized) {
          setShowSetupModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }, []);

  const fetchBalanceCheck = useCallback(async () => {
    try {
      const res = await fetch(API_ROUTES.reports.balanceCheck());
      const data = await res.json() as BalanceCheckResult;
      setBalanceCheck(data);
    } catch (error) {
      console.error('Failed to fetch balance check:', error);
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch('/api/accounts/all');
      const data = await res.json() as { accounts: Account[] };
      setAccounts(data.accounts);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJournal = useCallback(async (status: 'draft' | 'posted') => {
    const setEntries = status === 'draft' ? setJournalDrafts : setPostedEntries;
    const setLoad = status === 'draft' ? setDraftsLoading : setPostedLoading;
    setLoad(true);
    try {
      const res = await fetch(`/api/journal?status=${status}`);
      const data = await res.json() as { entries: JournalEntry[] };
      setEntries(data.entries || []);
    } catch (error) {
      console.error(`Failed to fetch ${status} journal entries:`, error);
    } finally {
      setLoad(false);
    }
  }, []);

  const fetchTrialBalance = useCallback(async () => {
    try {
      const res = await fetch('/api/trial-balance');
      const data = await res.json() as { rows: TrialBalanceRow[]; totals: TrialBalanceTotals };
      setTrialRows(data.rows || []);
      setTrialTotals(data.totals || { debit: 0, credit: 0 });
    } catch (error) {
      console.error('Failed to fetch trial balance:', error);
    }
  }, []);

  const fetchPeriods = useCallback(async () => {
    try {
      const res = await fetch('/api/periods');
      const data = await res.json() as { periods: PeriodClosure[]; monthsWithEntries: string[] };
      setPeriods(data.periods || []);
      setMonthsWithEntries(data.monthsWithEntries || []);
    } catch (error) {
      console.error('Failed to fetch period closures:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch('/api/oms/customers');
      const data = await res.json() as { customers: Customer[] };
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await fetch('/api/oms/products');
      const data = await res.json() as { products: Product[] };
      setProducts(data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, []);

  const fetchCoupons = useCallback(async () => {
    try {
      const res = await fetch('/api/coupons');
      const data = await res.json() as { coupons: Coupon[] };
      setCoupons(data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  }, []);

  // Initial data fetch (permission-aware)
  // Fetch all permitted data once permissions are loaded
  useEffect(() => {
    // Wait for permissions to load before fetching data
    if (permissionsLoading) {
      return;
    }

    fetchProfile();
    
    // Only fetch data if user has permission
    if (checkAccess('finance/accounts', 'read') || checkAccess('finance/coa', 'read')) {
      fetchAccounts();
      fetchBalanceCheck();
    }
    if (checkAccess('finance/reports', 'read')) {
      fetchTrialBalance();
    }
    if (checkAccess('finance/periods', 'read')) {
      fetchPeriods();
    }
    if (checkAccess('oms/customers', 'read')) {
      fetchCustomers();
    }
    if (checkAccess('oms/products', 'read')) {
      fetchProducts();
    }
    if (checkAccess('oms/coupons', 'read')) {
      fetchCoupons();
    }
  }, [permissionsLoading, checkAccess, fetchProfile, fetchAccounts, fetchBalanceCheck, fetchTrialBalance, fetchPeriods, fetchCustomers, fetchProducts, fetchCoupons]);

  // Fetch journal data only after profile is loaded and initialized
  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (userProfile?.profileInitialized && checkAccess('finance/journal', 'read')) {
      fetchJournal('draft');
      fetchJournal('posted');
    }
  }, [userProfile, permissionsLoading, checkAccess, fetchJournal]);

  // Refetch data based on current route (optimization)
  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    const parentSection = pathParts[0];

    if (parentSection === 'finance' && userProfile?.profileInitialized) {
      if (checkAccess('finance/journal', 'read')) {
        fetchJournal('draft');
        fetchJournal('posted');
      }
      if (checkAccess('finance/reports', 'read')) {
        fetchTrialBalance();
      }
      if (checkAccess('finance/periods', 'read')) {
        fetchPeriods();
      }
      if (checkAccess('finance/accounts', 'read') || checkAccess('finance/coa', 'read')) {
        fetchAccounts();
      }
    } else if (parentSection === 'admin') {
      if (checkAccess('finance/accounts', 'read') || checkAccess('finance/coa', 'read')) {
        fetchAccounts();
      }
    } else if (parentSection === 'orders' || parentSection === 'oms') {
      if (checkAccess('oms/customers', 'read')) {
        fetchCustomers();
      }
      if (checkAccess('oms/products', 'read')) {
        fetchProducts();
      }
      if (checkAccess('oms/coupons', 'read')) {
        fetchCoupons();
      }
    }
    if (checkAccess('finance/accounts', 'read') || checkAccess('finance/coa', 'read')) {
      fetchBalanceCheck();
    }
  }, [location.pathname, userProfile, permissionsLoading, checkAccess, fetchJournal, fetchTrialBalance, fetchPeriods, fetchAccounts, fetchCustomers, fetchProducts, fetchCoupons, fetchBalanceCheck]);

  // Handler functions for finance operations
  const handleAdd = async (payload: JournalDraftInput) => {
    const res = await fetch('/api/journal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(err.error || 'Failed to save journal draft');
    }

    const result = await res.json() as { ok: boolean; id: string };
    await fetchJournal('draft');
    await fetchBalanceCheck();
    return result;
  };

  const handlePostDraft = async (id: string) => {
    const res = await fetch(`/api/journal/${id}/post`, { method: 'POST' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to post journal entry');
    }
    await fetchJournal('draft');
    await fetchJournal('posted');
    await fetchTrialBalance();
    await fetchBalanceCheck();
  };

  const handleEditDraft = async (id: string, payload: JournalDraftInput) => {
    const res = await fetch(`/api/journal/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to update journal draft');
    }
    await fetchJournal('draft');
    await fetchBalanceCheck();
  };

  const handleDeleteDraft = async (id: string) => {
    const res = await fetch(`/api/journal/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to delete journal draft');
    }
    await fetchJournal('draft');
    await fetchBalanceCheck();
  };

  const handleReverse = async (id: string) => {
    const res = await fetch(`/api/journal/${id}/reverse`, { method: 'POST' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to reverse journal entry');
    }
    await fetchJournal('draft');
    await fetchJournal('posted');
    await fetchTrialBalance();
    await fetchBalanceCheck();
  };

  const handleClosePeriod = async (period: string) => {
    const res = await fetch(`/api/periods/${period}/close`, { method: 'POST' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to close period');
    }
    await fetchPeriods();
  };

  const handleOpenPeriod = async (period: string, reason?: string) => {
    const res = await fetch(`/api/periods/${period}/close`, {
      method: 'DELETE',
      headers: reason ? { 'Content-Type': 'application/json' } : {},
      body: reason ? JSON.stringify({ reason }) : undefined
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to reopen period');
    }
    await fetchPeriods();
  };

  const handleSaveAccount = async (accountData: Partial<Account>) => {
    const isEdit = accountData.id && accounts.some(a => a.id === accountData.id);
    
    // Auto-generate code if creating new account and code is empty
    if (!isEdit && !accountData.code?.trim()) {
      const categoryPrefix = accountData.category?.substring(0, 1).toUpperCase() || 'X';
      const namePrefix = accountData.name?.substring(0, 2).toUpperCase() || 'XX';
      const timestamp = Date.now().toString().slice(-3);
      accountData.code = `${categoryPrefix}${namePrefix}${timestamp}`;
    }
    
    const res = await fetch(isEdit ? `/api/accounts/${accountData.id}` : '/api/accounts', {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(accountData)
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({})) as { error?: string | { fieldErrors?: Record<string, string[]> } };
      console.error('API Error:', res.status, errorData);
      let errorMessage = typeof errorData.error === 'string' ? errorData.error : 'Failed to save account';
      
      // Parse Zod validation errors from fieldErrors object
      if (errorData.error && typeof errorData.error === 'object' && 'fieldErrors' in errorData.error) {
        const fieldErrors = errorData.error.fieldErrors;
        if (fieldErrors) {
          const errorList = Object.entries(fieldErrors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
              return `${fieldName}: ${messages.join(', ')}`;
            })
            .join('; ');
          errorMessage = `Validation failed: ${errorList}`;
        }
      }
      throw new Error(errorMessage);
    }
    
    const result = await res.json() as { ok: boolean; newId?: string };
    await fetchAccounts();
    
    // Refetch journal entries if code changed to reflect updated account_id
    if (result.newId) {
      await fetchJournal('draft');
      await fetchJournal('posted');
    }
    await fetchBalanceCheck();
  };

  const handleDeleteAccount = async (id: string) => {
    const res = await fetch(`/api/accounts/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to deactivate account');
    }
    await fetchAccounts();
    await fetchBalanceCheck();
  };

  const handleHardDeleteAccount = async (id: string) => {
    const res = await fetch(`/api/accounts/${id}/hard-delete`, { method: 'DELETE' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to hard delete account');
    }
    await fetchAccounts();
    await fetchBalanceCheck();
  };

  const handleReactivateAccount = async (id: string) => {
    const res = await fetch(`/api/accounts/${id}/reactivate`, { method: 'PATCH' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(errData.error || 'Failed to reactivate account');
    }
    await fetchAccounts();
    await fetchBalanceCheck();
  };

  // Common props for finance views
  const financeProps = {
    accounts,
    journalDrafts,
    draftsLoading,
    postedEntries,
    postedLoading,
    trialRows,
    trialTotals,
    periods,
    monthsWithEntries,
    loading,
    onAddDraft: handleAdd,
    onPostDraft: handlePostDraft,
    onEditDraft: handleEditDraft,
    onDeleteDraft: handleDeleteDraft,
    onReverse: handleReverse,
    onClosePeriod: handleClosePeriod,
    onOpenPeriod: handleOpenPeriod,
    onSaveAccount: handleSaveAccount,
    onDeleteAccount: handleDeleteAccount,
    onHardDeleteAccount: handleHardDeleteAccount,
    onReactivateAccount: handleReactivateAccount,
  };

  // Show authentication loading state while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Verifying your access...</p>
          <p className="text-slate-500 text-sm mt-1">This may take a moment</p>
        </div>
      </div>
    );
  }

  // Show error state if authentication failed
  if (permissions.error && permissions.error.includes('Failed to fetch')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="rounded-full bg-red-100 w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Authentication Required</h2>
          <p className="text-slate-600 mb-6">Your session has expired or you are not authenticated. Please sign in again to continue.</p>
          <button
            onClick={() => {
              window.location.href = '/';
            }}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Sign In Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />

      {/* Main content area - offset by sidebar width */}
      <div 
        className="transition-all duration-300"
        style={{ marginLeft: sidebarCollapsed ? '4rem' : '16rem' }}
      >
        {/* Compact header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold" style={{ color: branding.primaryColor }}>
                {branding.companyName}
              </h1>
            </div>

            {/* Balance Check Alert (Inline in header) */}
            {balanceCheck && !balanceCheck.isBalanced && (
              <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-red-800">
                  {balanceCheck.discrepancies.length} Imbalance{balanceCheck.discrepancies.length > 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Profile Button */}
            {userProfile && (
              <button
                className="relative w-10 h-10 rounded-full border-2 border-slate-200 flex items-center justify-center bg-white hover:shadow transition-shadow"
                onClick={() => navigate('/admin/profile')}
                title="Profile"
              >
                {userProfile.profilePicture ? (
                  <img src={userProfile.profilePicture} alt={userProfile.displayName} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <span className="text-lg font-semibold text-blue-700">
                    {userProfile.displayName?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                  </span>
                )}
              </button>
            )}
          </div>
        </header>

        {/* Permissions loading/error state */}
        {permissionsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading permissions...</p>
            </div>
          </div>
        ) : permissions.error ? (
          <div className="flex items-center justify-center py-12">
            <div className="max-w-md">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-red-800 mb-2">Permission Error</h3>
                    <p className="text-sm text-red-700">{permissions.error}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <main className="p-6">
            {/* Breadcrumb Navigation */}
            <Breadcrumb />

            {/* Route Content */}
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                {/* Root redirects to Dashboard Overview */}
                <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />

                {/* Dashboard Routes */}
                <Route path="/dashboard/overview" element={<DashboardView />} />
                <Route path="/dashboard/financial" element={<FinancialDashboardView />} />
                <Route path="/dashboard/kanban" element={<KanbanBoardView />} />

                {/* Finance Routes */}
                <Route path="/finance/journal" element={<JournalView {...financeProps} />} />
                <Route path="/finance/transactions" element={<TransactionsView postedEntries={postedEntries} postedLoading={postedLoading} onReverse={handleReverse} />} />
                <Route path="/finance/coa" element={<AccountsView accounts={accounts} loading={loading} onSave={handleSaveAccount} onDelete={handleDeleteAccount} onHardDelete={handleHardDeleteAccount} onReactivate={handleReactivateAccount} />} />
                <Route path="/finance/reports" element={<ReportsView userProfile={userProfile} />} />
                <Route path="/finance/reconciliation" element={<ReconciliationView accounts={accounts} />} />
                <Route path="/finance/import" element={<ImportView accounts={accounts} onAccountsUpdated={fetchAccounts} onJournalUpdated={async () => { await fetchJournal('draft'); await fetchJournal('posted'); await fetchTrialBalance(); await fetchBalanceCheck(); }} />} />

                {/* Enhanced Finance Routes */}
                <Route path="/finance/enhanced/dashboard" element={<EnhancedFinanceDashboard periods={periods} monthsWithEntries={monthsWithEntries} />} />
                <Route path="/finance/enhanced/entries/journal" element={<JournalEntriesWithSearch {...financeProps} />} />
                <Route path="/finance/enhanced/entries/posted" element={<PostedEntriesWithSearch postedEntries={postedEntries} postedLoading={postedLoading} onReverse={handleReverse} />} />
                <Route path="/finance/enhanced/entries/templates" element={<EntryTemplatesView />} />
                <Route path="/finance/enhanced/accounts/coa" element={<AccountsView accounts={accounts} loading={loading} onSave={handleSaveAccount} onDelete={handleDeleteAccount} onHardDelete={handleHardDeleteAccount} onReactivate={handleReactivateAccount} />} />
                <Route path="/finance/enhanced/accounts/reconciliation" element={<AccountReconciliationHub accounts={accounts} />} />
                <Route path="/finance/enhanced/periods" element={<PeriodHealthDashboard periods={periods} monthsWithEntries={monthsWithEntries} />} />
                <Route path="/finance/enhanced/reports" element={<ReportsView userProfile={userProfile} />} />
                <Route path="/finance/enhanced/reports/balance-history" element={<AccountBalanceHistory accounts={accounts} />} />
                <Route path="/finance/enhanced/reports/audit" element={<EntryPostingAuditTrail />} />
                <Route path="/finance/enhanced/operations" element={<BatchOperationsView {...financeProps} />} />
                <Route path="/tracking/time" element={<TimeTrackingView />} />
                <Route path="/tracking/mileage" element={<MileageView />} />

                {/* Orders Routes */}
                <Route path="/orders/orders" element={<OrdersView customers={customers} products={products} />} />
                <Route path="/orders/customers" element={<CustomersView />} />
                <Route path="/orders/products" element={<ProductsView />} />
                <Route path="/orders/coupons" element={<CouponsView />} />
                <Route path="/orders/inventory" element={<InventoryView />} />

                {/* Admin Routes */}
                <Route path="/admin/etsy" element={<EtsyIntegrationView />} />
                <Route path="/admin/etsy-import" element={<EtsyImportView products={products} customers={customers} coupons={coupons} orders={[]} onImportComplete={() => { fetchProducts(); fetchCustomers(); fetchCoupons(); }} />} />
                <Route path="/admin/audit" element={<AuditLogView />} />
                <Route path="/admin/users" element={<UsersView />} />
                <Route path="/admin/roles" element={<RolePermissionsView />} />
                <Route path="/admin/profile" element={<ProfileView onProfileUpdated={fetchProfile} />} />
                <Route path="/admin/settings" element={<AdminSettingsView />} />

                {/* Workshop Routes */}
                <Route path="/workshop/printers" element={<PrintersView />} />
                <Route path="/workshop/spools" element={<SpoolsView />} />
                <Route path="/workshop/print-queue" element={<PrintQueueView />} />
                <Route path="/workshop/maintenance" element={<MaintenanceLogView />} />
                <Route path="/workshop/inventory" element={<WorkshopInventoryView />} />

                {/* Help - available to all authenticated users */}
                <Route path="/help" element={<HelpView />} />

                {/* Catch-all 404 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </main>
        )}
      </div>

      {/* Profile Setup Modal */}
      {showSetupModal && userProfile && (
        <ProfileSetupModal
          email={userProfile.email}
          defaultFirstName={userProfile.firstName}
          defaultLastName={userProfile.lastName}
          onComplete={() => {
            setShowSetupModal(false);
            fetchProfile();
          }}
        />
      )}
    </div>
  );
}

/**
 * App - Main application component
 * Now using BrowserRouter from main.tsx for proper routing
 */
function App() {
  return <AppContent />;
}

export default App;
